import Link from "next/link"
import Image from "next/image"
import { Briefcase, Plus, Pencil, Store, Eye } from "lucide-react"
import { TalentFeed } from "@/components/network/talent-feed"

// Owner-mode rendering of /hiring. Shows ONLY this owner's job
// listings across their shops, plus a CTA to post a new one or set up
// a shop if they have none yet. Server-rendered, role-gated upstream.

export interface OwnerListing {
  id: string
  title: string
  is_published: boolean
  filled_at: string | null
  created_at: string
  position_type: string[]
  compensation: string | null
  view_count: number | null
  shop: {
    id: string
    slug: string | null
    name: string
    logo_url: string | null
    city: string | null
    state: string | null
  } | null
}

interface OwnerShopOption {
  id: string
  slug: string | null
  name: string
  logo_url: string | null
}

interface OwnerHiringViewProps {
  listings: OwnerListing[]
  shops: OwnerShopOption[]
}

type BadgeVariant = "active" | "muted"

// Three states, all owner-driven. Postings don't expire on their own
// (migration 1031): one is live until the owner fills, unpublishes or
// deletes it, so there's no "Expired" here and no countdown.
function statusBadge(l: OwnerListing): { label: string; variant: BadgeVariant } {
  if (l.filled_at) return { label: "Filled", variant: "muted" }
  if (!l.is_published) return { label: "Draft", variant: "muted" }
  return { label: "Active", variant: "active" }
}

// Matches the per-shop Hiring page (/dashboard/owner/shop/[id]/hiring)
// so a single visual badge appears across every owner hiring surface.
// Tightened to text-[8px] / px-1 / gap-0.5 per design — was px-2 py-0.5
// which read too prominent next to the shop name.
function renderBadge(b: { label: string; variant: BadgeVariant }) {
  if (b.variant === "active") {
    return (
      <span className="text-xs font-light uppercase tracking-wider text-booth-green">
        {b.label}
      </span>
    )
  }
  return <span className="text-xs font-light uppercase tracking-wider text-muted-foreground">{b.label}</span>
}

export function OwnerHiringView({ listings, shops }: OwnerHiringViewProps) {
  const newPostingHref =
    shops.length === 1
      ? `/dashboard/owner/shop/${shops[0].id}/hiring/new`
      : shops.length > 1
      ? `/dashboard/owner/shop/${shops[0].id}/hiring/new` // pick first; user can switch shops on shop dashboard
      : null

  return (
    // Standard authed-page chrome. The old md:px-28 / lg:px-32 here was a
    // looser copy of the gutter that cleared the top-left nav pill, and it
    // stacked on top of the gutter PageFrame already applies.
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      <main className="px-4 sm:px-6 pt-24 sm:pt-24 pb-28 md:px-0 md:pt-20">
        {/* Centered on the true viewport (aligned with the centered BOOTH
            wordmark), per the dashboard content-centering rule. Narrowed to
            max-w-2xl for a more refined, less-wide hiring column. */}
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-xl font-bold tracking-tight text-foreground">Hiring</h1>
              <p className="text-sm text-gray-500 mt-1">
                Manage your open positions and source barbers across all your shops.
              </p>
            </div>
            {newPostingHref && (
              <Link
                href={newPostingHref}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-black text-white text-xs font-medium tracking-wide ring-1 ring-inset ring-white/10 hover:bg-zinc-900 transition-colors flex-shrink-0"
              >
                <Plus className="w-3.5 h-3.5" />
                New Job Posting
              </Link>
            )}
          </div>

          {/* Preview-the-public-board link. Owners normally see only their
              own postings here; this lets them peek at the public job
              board exactly as a barber would, but in read-only mode
              (Inquire is suppressed via ?preview=1). */}
          <div className="mb-6">
            <Link
              href="/hiring?preview=1"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 transition-colors"
            >
              <Eye className="w-3.5 h-3.5" strokeWidth={1.75} />
              View public job board
            </Link>
          </div>

          {/* A shopless owner never reaches this view — HiringRouter sends
              them to the job board with a create-a-shop prompt instead of the
              full-viewport wall that used to stand here. */}
          {listings.length === 0 ? (
            /* No listings yet */
            <div className="text-center py-16 border border-dashed border-border rounded-[2.5rem] bg-card dark:bg-neutral-950">
              <Briefcase className="w-9 h-9 text-gray-400 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-base font-semibold mb-1">No postings yet</p>
              <p className="text-xs text-gray-500 mb-5 max-w-sm mx-auto">
                Post your first hiring listing to start receiving inquiries from barbers on BOOTH.
              </p>
              {newPostingHref && (
                <Link
                  href={newPostingHref}
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-black text-white text-xs font-medium hover:bg-zinc-900 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  New Job Posting
                </Link>
              )}
            </div>
          ) : (
            /* Listings list — grouped visually by shop label */
            <div className="space-y-2">
              {listings.map((l) => {
                const badge = statusBadge(l)
                const editHref = l.shop
                  ? `/dashboard/owner/shop/${l.shop.id}/hiring/${l.id}/edit`
                  : "#"
                return (
                  <article key={l.id} className="rounded-[2.5rem] px-1 py-3 hover:bg-muted/20 transition-colors">
                    <div className="flex items-start gap-3">
                      {l.shop?.logo_url ? (
                        <div className="relative w-10 h-10 rounded-full overflow-hidden bg-muted dark:bg-neutral-900 border border-border flex-shrink-0">
                          <Image src={l.shop.logo_url} alt={l.shop.name} fill className="object-cover" sizes="40px" />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-muted dark:bg-neutral-900 flex items-center justify-center flex-shrink-0">
                          <Store className="w-4 h-4 text-muted-foreground" />
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          <span className="text-[12px] font-semibold truncate">{l.shop?.name || "Shop"}</span>
                          {renderBadge(badge)}
                        </div>
                        <h3 className="text-[14px] font-semibold leading-snug">{l.title}</h3>
                        <div className="flex flex-wrap gap-1.5 mt-1.5 text-xs text-gray-500 capitalize">
                          {l.position_type.length > 0 && <span>{l.position_type.map(p => p.replace("_", " ")).join(" · ")}</span>}
                          {l.compensation && <span>· {l.compensation.replace("_", " ")}</span>}
                        </div>
                        <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-gray-500">
                          <Eye className="w-3.5 h-3.5" strokeWidth={1.75} />
                          {(l.view_count ?? 0).toLocaleString()} {(l.view_count ?? 0) === 1 ? "view" : "views"}
                        </div>
                      </div>

                      {/* Edit — top-right, in line with the name/status row (self-start);
                          white button + blue text, small pencil. App-wide edit convention. */}
                      <Link
                        href={editHref}
                        className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-full border border-border bg-white text-booth-blue text-xs font-medium hover:border-booth-blue/40 transition-colors flex-shrink-0 self-start"
                      >
                        <Pencil className="w-3 h-3" strokeWidth={1.75} />
                        <span className="hidden sm:inline">Edit</span>
                      </Link>
                    </div>
                  </article>
                )
              })}
            </div>
          )}

          {/* Community talent — the exact Network Talent feed, so owners can
              source barbers without leaving Hiring. */}
          {shops.length > 0 && (
            <div className="mt-12">
              <TalentFeed />
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
